/**
 * Loader for the parametric human built by tools/build_human.py (MakeHuman CC0 data):
 * one JSON manifest describing typed arrays packed back to back in human.bin.
 */

import { retry } from '../engine/retry';
import { LITE } from '../lite';

export interface ArrayRef { offset: number; count: number; type: 'f' | 'H' | 'I' | 'h' | 'B' }

export interface ModifierDef {
  group: string;
  id: string;
  label: string;
  bipolar: boolean;
  /** target names applied with |value| when value < 0 (bipolar) */
  min: string[];
  /** target names applied with value when value > 0 (or 0..1 for unipolar) */
  max: string[];
}

export interface ProxyDef {
  kind: 'eyebrows' | 'eyelashes' | 'eyes' | 'teeth' | 'hair' | 'clothes';
  name: string;
  texture: string | null;
  transparent: boolean;
  zDepth: number;
  renderToSrc: ArrayRef;
  uv: ArrayRef;
  index: ArrayRef;
  refs: ArrayRef;
  weights: ArrayRef;
  offsets: ArrayRef;
  scales: Partial<Record<'x' | 'y' | 'z', [number, number, number]>>;
  deleteVerts: number[];
  /** hair: its normal map (the strands' relief) */
  normal?: string;
  /** the live build: its arrays are in a file of their own (p/<name>.binz, gzipped; fetched when it's first worn), and its
   *  refs point into that; without it (dev), into human.bin */
  file?: string;
}

export interface BoneDef { name: string; head: string; tail: string; parent: string | null; rotation_plane: string; reference: string | null }

export interface HumanManifest {
  vertexCount: number;
  deltaScale: number;
  base: ArrayRef;
  body: { renderToSrc: ArrayRef; uv: ArrayRef; index: ArrayRef };
  targets: { name: string; start: number; count: number }[];
  targetIndex: ArrayRef;
  targetDelta: ArrayRef;
  modifiers: ModifierDef[];
  rig: { bones: BoneDef[]; joints: Record<string, number[]>; planes: Record<string, string[]>; skinIndex: ArrayRef; skinWeight: ArrayRef };
  skins: { age: string; race: string; sex: string; texture: string }[];
  proxies: ProxyDef[];
  eyeColours: Record<string, string>;
}

export interface HumanData {
  manifest: HumanManifest;
  base: Float32Array;
  targetIndex: Uint16Array;
  targetDelta: Int16Array;
  targets: Map<string, { start: number; count: number }>;
  /** typed view for any ArrayRef in the manifest (a proxy's: proxyView) */
  view<T extends ArrayRef>(ref: T): Float32Array | Uint16Array | Uint32Array | Int16Array | Uint8Array;
  /** a proxy's arrays are here (always, without a file of its own) */
  hasProxy(def: ProxyDef): boolean;
  /** fetch a proxy's own file (once; tried again on a bad connection) */
  loadProxy(def: ProxyDef): Promise<void>;
  /** typed view for one of a proxy's refs (its file once loaded, else human.bin) */
  proxyView(def: ProxyDef, ref: ArrayRef): Float32Array | Uint16Array | Uint32Array | Int16Array | Uint8Array;
  url(rel: string): string;
}

const CTOR = { f: Float32Array, H: Uint16Array, I: Uint32Array, h: Int16Array, B: Uint8Array } as const;

let cached: Promise<HumanData> | undefined;

/**
 * The packed arrays. The live build gzips human.bin and splits it into parts (the host won't compress a .bin, and caps a
 * file at 25 MB: Cloudflare Pages), listed in human.parts.json; they're joined and unzipped here. Without it (dev), the one file.
 */
async function binary(base: string, name = 'human'): Promise<ArrayBuffer> {
  const list = await fetch(`${base}${name}.parts.json`).then((r) => (r.ok ? (r.json() as Promise<string[] | { gzip: boolean; parts: string[] }>) : null)).catch(() => null);
  const parts = Array.isArray(list) ? list : list?.parts;
  // (a failed status is a failure: an error page's bytes, unzipped as the body, failed later with a riddle)
  const got = (u: string) => fetch(u).then((r) => { if (!r.ok) throw new Error(`${u}: ${r.status}`); return r.arrayBuffer(); });
  if (!parts?.length) return got(`${base}human.bin`);
  const bufs = await Promise.all(parts.map((f) => got(`${base}${f}`)));
  const joined = new Blob(bufs);
  if (Array.isArray(list) || !list?.gzip) return joined.arrayBuffer();
  return new Response(joined.stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
}

export function loadHumanData(base = `${import.meta.env.BASE_URL}human/`): Promise<HumanData> {
  cached ??= (async () => {
    // (Unfold Lite: its own smaller core, if the build made one (tools/split-big.mjs); else, as in dev, the full one)
    const lite = LITE && (await fetch(`${base}human-lite.parts.json`, { method: 'HEAD' }).then((r) => r.ok && /json/.test(r.headers.get('content-type') ?? ''), () => false));
    const name = lite ? 'human-lite' : 'human';
    const [manifest, bin] = await Promise.all([
      fetch(`${base}${name}.json`).then((r) => { if (!r.ok) throw new Error(`${name}.json: ${r.status}`); return r.json() as Promise<HumanManifest>; }),
      binary(base, name),
    ]);
    const view = (ref: ArrayRef) => new CTOR[ref.type](bin, ref.offset, ref.count);
    // (each outfit, hairstyle, the eyes...: its own small gzipped file, when it's first worn)
    const own = new Map<string, ArrayBuffer>(), pending = new Map<string, Promise<void>>();
    const loadProxy = (def: ProxyDef): Promise<void> => {
      if (!def.file || own.has(def.name)) return Promise.resolve();
      let p = pending.get(def.name);
      if (!p) {
        p = retry(async () => {
          const r = await fetch(`${base}${def.file}`);
          if (!r.ok) throw new Error(`${def.file}: ${r.status}`);
          // (gzipped, unless the host or the browser has unzipped it already: look for gzip's first two bytes)
          const buf = await r.arrayBuffer(), b = new Uint8Array(buf, 0, Math.min(2, buf.byteLength));
          own.set(def.name, b[0] === 0x1f && b[1] === 0x8b ? await new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer() : buf);
        });
        pending.set(def.name, p);
        p.catch(() => pending.delete(def.name)); // (failed for good: the next time it's asked for, try again)
      }
      return p;
    };
    return {
      manifest,
      base: view(manifest.base) as Float32Array,
      targetIndex: view(manifest.targetIndex) as Uint16Array,
      targetDelta: view(manifest.targetDelta) as Int16Array,
      targets: new Map(manifest.targets.map((t) => [t.name, { start: t.start, count: t.count }])),
      view,
      hasProxy: (def) => !def.file || own.has(def.name),
      loadProxy,
      proxyView: (def, ref) => { const b = def.file ? own.get(def.name) : bin; if (!b) throw new Error(`${def.name} isn't loaded`); return new CTOR[ref.type](b, ref.offset, ref.count); },
      url: (rel: string) => base + rel,
    };
  })();
  // (a failed download isn't kept: the next ask tries again. Kept, every retry got the same failure back, and a
  // dropped connection at the start meant a stand-in body for the whole session)
  const mine = cached;
  mine.catch(() => { if (cached === mine) cached = undefined; });
  return cached;
}
